import mitt from 'mitt';
import * as SIP from 'sip.js';
import { referNotifyCause, inboundAutoRejectStatusCode } from './telephonyTransferHelpers';

const CONNECTION_STATUS = Object.freeze({
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
});

const CALL_STATE = Object.freeze({
  IDLE: 'idle',
  RINGING: 'ringing',
  CONNECTED: 'connected',
});

class SipClient {
  static instance = null;

  static getInstance() {
    if (!SipClient.instance) {
      SipClient.instance = new SipClient();
    }
    return SipClient.instance;
  }

  get currentSession() {
    return this.primarySession;
  }

  set currentSession(session) {
    this.primarySession = session;
  }

  constructor() {
    this.emitter = mitt();

    this.userAgent = null;
    this.registerer = null;
    this.primarySession = null;
    this.transferSession = null;
    this._transferPrimary = null;
    this._transferCompletionPending = false;
    this._transferIncomingContext = null;

    this.remoteAudioEl = null;
    this.remoteStream = null;

    this.connectionStatus = CONNECTION_STATUS.DISCONNECTED;
    this.isRegistered = false;

    this.shouldBeConnected = false;
    this.attemptingReconnection = false;
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    this.registerRetryTimer = null;

    this.callTimerInterval = null;
    this.callStartTime = null;
    this.isMuted = false;
    this.currentDirection = null; // 'inbound' | 'outbound'
    this.currentRemoteNumber = null;
    this.endedReason = null;
    this.lastSipStatusCode = null;
    this.lastSipReasonPhrase = null;

    // local mic (optional): used to validate mic access and help debugging
    this.localStream = null;
    this._micWarmupPromise = null;

    // ringback (outbound)
    this._ringback = {
      ctx: null,
      oscA: null,
      oscB: null,
      gain: null,
      timer: null,
      isPlaying: false,
    };
    this._ringbackGeneration = 0;

    // remote audio wiring guard (avoid double-binding)
    this._remoteAudioBoundPc = null;

    // avoid duplicate call:ended emissions when both onReject and Terminated fire
    this._callEndedForSessionId = null;

    // force-cleanup after hangup if SIP never reaches Terminated
    this._hangupForceTimer = null;

    // abort in-flight REFER wait when user hangs up
    this._transferAbort = false;
  }

  on(eventName, handler) {
    this.emitter.on(eventName, handler);
    return () => this.emitter.off(eventName, handler);
  }

  emit(eventName, payload) {
    this.emitter.emit(eventName, payload);
  }

  setRemoteAudioElement(audioEl) {
    this.remoteAudioEl = audioEl || null;
  }

  setAvailabilityProvider(fn) {
    this._availabilityProvider = fn;
  }

  unlockAudioPlayback() {
    if (!this.remoteAudioEl) return;
    try {
      // Always kick play() (autoplay gesture), but only unmute when remote media is bound
      // and the call is still live — hangup/cancel can race the play() promise.
      this.remoteAudioEl.muted = true;
      const p = this.remoteAudioEl.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          if (!this.remoteAudioEl || !this.primarySession || !this._remoteAudioBoundPc) return;
          this.remoteAudioEl.muted = false;
        }).catch(() => {});
      } else if (this.primarySession && this._remoteAudioBoundPc) {
        this.remoteAudioEl.muted = false;
      }
    } catch {
      // noop
    }
  }

  /**
   * Drop early-media / remote audio immediately.
   * Outbound PSTN ringback often arrives as RTP (183 + earlyMedia), not WebAudio —
   * stopping oscillators alone leaves the agent hearing trunk ringback.
   */
  detachRemoteAudio() {
    const el = this.remoteAudioEl;
    if (el) {
      try {
        el.pause();
      } catch {
        // ignore
      }
      try {
        el.srcObject = null;
      } catch {
        // ignore
      }
      try {
        el.muted = true;
      } catch {
        // ignore
      }
    }
    this.remoteStream = null;

    const pc = this._remoteAudioBoundPc;
    this._remoteAudioBoundPc = null;
    if (pc) {
      try {
        pc.ontrack = null;
      } catch {
        // ignore
      }
      try {
        pc.getReceivers().forEach(receiver => {
          try {
            receiver.track?.stop();
          } catch {
            // ignore
          }
        });
      } catch {
        // ignore
      }
    }
  }

  // Local ringback + early-media remote audio.
  silenceCallAudio() {
    this.stopRingback();
    this.detachRemoteAudio();
  }

  startRingback() {
    if (this._ringback.isPlaying) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    try {
      const ctx = new AudioCtx();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gain.connect(ctx.destination);

      // Typical ringback: mix of 440Hz + 480Hz, 2s ON / 4s OFF
      const oscA = ctx.createOscillator();
      oscA.type = 'sine';
      oscA.frequency.value = 440;
      oscA.connect(gain);

      const oscB = ctx.createOscillator();
      oscB.type = 'sine';
      oscB.frequency.value = 480;
      oscB.connect(gain);

      oscA.start();
      oscB.start();

      // Local generation flag: nested timers must not revive audio after stopRingback.
      const generation = (this._ringbackGeneration || 0) + 1;
      this._ringbackGeneration = generation;

      const pattern = () => {
        if (this._ringbackGeneration !== generation || !this._ringback.isPlaying) return;
        // ON
        gain.gain.setTargetAtTime(0.08, ctx.currentTime, 0.01);
        this._ringback.timer = setTimeout(() => {
          if (this._ringbackGeneration !== generation || !this._ringback.isPlaying) return;
          // OFF
          gain.gain.setTargetAtTime(0.0, ctx.currentTime, 0.01);
          this._ringback.timer = setTimeout(pattern, 4000);
        }, 2000);
      };

      this._ringback = {
        ctx,
        oscA,
        oscB,
        gain,
        timer: null,
        isPlaying: true,
      };

      // Resume if needed (should be a user gesture on click-to-call).
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      pattern();
    } catch {
      // noop
    }
  }

  stopRingback() {
    // Bump generation first so any in-flight pattern timeouts become no-ops.
    this._ringbackGeneration = (this._ringbackGeneration || 0) + 1;
    if (!this._ringback.isPlaying) {
      this._ringback = {
        ctx: null,
        oscA: null,
        oscB: null,
        gain: null,
        timer: null,
        isPlaying: false,
      };
      return;
    }
    try {
      if (this._ringback.timer) clearTimeout(this._ringback.timer);
      if (this._ringback.gain && this._ringback.ctx) {
        this._ringback.gain.gain.setTargetAtTime(0.0, this._ringback.ctx.currentTime, 0.01);
      }
      if (this._ringback.oscA) this._ringback.oscA.stop();
      if (this._ringback.oscB) this._ringback.oscB.stop();
      if (this._ringback.ctx) this._ringback.ctx.close().catch(() => {});
    } catch {
      // ignore
    } finally {
      this._ringback = {
        ctx: null,
        oscA: null,
        oscB: null,
        gain: null,
        timer: null,
        isPlaying: false,
      };
    }
  }

  playTransferReturnBeep() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    try {
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      gain.gain.value = 0.12;
      osc.connect(gain);
      gain.connect(ctx.destination);
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      osc.start();
      const stopAt = ctx.currentTime + 0.18;
      gain.gain.setTargetAtTime(0, stopAt, 0.02);
      osc.stop(stopAt + 0.05);
      setTimeout(() => {
        try {
          ctx.close();
        } catch (e) {
          // ignore
        }
      }, 300);
    } catch (e) {
      // beep is best-effort UX
    }
  }

  bindRemoteAudio(session) {
    const pc = session?.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    if (this.primarySession !== session) return;
    if (
      session.state === SIP.SessionState.Terminated ||
      session.state === SIP.SessionState.Terminating
    ) {
      return;
    }
    if (this._remoteAudioBoundPc === pc) return;
    this._remoteAudioBoundPc = pc;

    this.remoteStream = new MediaStream();
    const attachRemoteStream = stream => {
      // Hangup/cancel may race late ontrack — never reattach after detach.
      if (this.primarySession !== session || this._remoteAudioBoundPc !== pc) return;
      if (!this.remoteAudioEl) return;
      this.remoteAudioEl.srcObject = stream;
      this.unlockAudioPlayback();
    };

    pc.getReceivers().forEach(r => {
      if (r.track) this.remoteStream.addTrack(r.track);
    });
    attachRemoteStream(this.remoteStream);

    pc.ontrack = event => {
      if (this.primarySession !== session || this._remoteAudioBoundPc !== pc) return;
      if (event.streams?.[0]) {
        attachRemoteStream(event.streams[0]);
        return;
      }
      if (event.track && this.remoteStream) {
        this.remoteStream.addTrack(event.track);
        attachRemoteStream(this.remoteStream);
      }
    };
  }

  mapSipStatusToReason(statusCode) {
    const code = Number(statusCode);
    if (!code) return 'failed';
    if (code === 487) return 'cancelled';
    if (code === 603) return 'rejected';
    if (code === 486 || code === 600) return 'busy';
    if (code === 408 || code === 480) return 'no_answer';
    if (code >= 300) return 'failed';
    return 'terminated';
  }

  emitCallEndedOnce({ session, durationSec }) {
    const sessionId = session?.id || null;
    if (sessionId && this._callEndedForSessionId === sessionId) return;
    if (sessionId) this._callEndedForSessionId = sessionId;

    this.emit('call:ended', {
      direction: this.currentDirection,
      remoteNumber: this.currentRemoteNumber,
      durationSec: typeof durationSec === 'number' ? durationSec : 0,
      reason: this.endedReason || 'terminated',
      sipStatusCode: this.lastSipStatusCode,
      sipReasonPhrase: this.lastSipReasonPhrase,
    });
  }

  isLocalhostLike(hostname) {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
  }

  getUserMediaCompat(constraints) {
    const md = navigator.mediaDevices;
    if (md?.getUserMedia) return md.getUserMedia(constraints);
    const legacy =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;
    if (!legacy) return null;
    return new Promise((resolve, reject) => legacy.call(navigator, constraints, resolve, reject));
  }

  getMicrophoneCapability() {
    const hasGetUserMedia =
      (!!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function') ||
      typeof navigator.getUserMedia === 'function' ||
      typeof navigator.webkitGetUserMedia === 'function' ||
      typeof navigator.mozGetUserMedia === 'function' ||
      typeof navigator.msGetUserMedia === 'function';

    // En HTTP (no localhost), Chrome/Edge bloquean micrófono. Esto explica el síntoma:
    // - Tú escuchas al remoto (recv funciona)
    // - El remoto no te escucha (no hay track de salida)
    const secureOk = !!window.isSecureContext || this.isLocalhostLike(window.location.hostname);

    return { hasGetUserMedia, secureOk };
  }

  async ensureMicrophonePermission() {
    const { hasGetUserMedia, secureOk } = this.getMicrophoneCapability();
    if (!hasGetUserMedia) {
      throw new Error('No hay soporte de micrófono (getUserMedia) en este navegador.');
    }
    if (!secureOk) {
      throw new Error('El micrófono requiere HTTPS (contexto seguro). Abre Chatwoot en https:// o en localhost.');
    }

    const p = this.getUserMediaCompat({ audio: true, video: false });
    if (!p) throw new Error('No hay soporte de micrófono (getUserMedia) en este navegador.');

    try {
      // Guardamos stream para debug/estado (y para que el permiso quede concedido).
      // OJO: no lo cerramos aquí para evitar que algunos navegadores “duerman” el input inmediatamente.
      // Se libera en cleanupCall().
      this.localStream = await p;
      return true;
    } catch (e) {
      const name = e?.name || '';
      if (name === 'SecurityError') {
        throw new Error('El navegador bloqueó el micrófono por políticas de seguridad (HTTPS/Permissions-Policy).');
      }
      const suffix = name ? ` (${name})` : '';
      throw new Error(`Permiso de micrófono denegado o no disponible${suffix}.`);
    }
  }

  setConnectionStatus(status, error) {
    this.connectionStatus = status;
    this.emit('connection:status', { status, error });
  }

  setRegistered(isRegistered) {
    this.isRegistered = isRegistered;
    this.emit('registration:status', {
      status: isRegistered ? 'registered' : 'unregistered',
    });
  }

  // ponytail: tras WS drop, sip.js puede seguir en Registered sin re-emitir stateChange;
  // nuestro flag isRegistered queda false y ENSURE_CONNECTED/dial nunca avanzan.
  syncRegistrationFromRegisterer() {
    if (!this.registerer) return false;
    if (this.registerer.state === SIP.RegistererState.Registered) {
      this.setRegistered(true);
      this.setConnectionStatus(CONNECTION_STATUS.CONNECTED);
      return true;
    }
    return false;
  }

  async recoverRegistration() {
    if (this.syncRegistrationFromRegisterer()) return true;
    if (!this.shouldBeConnected || !this.userAgent || !this.registerer) return false;

    // Registerer Terminated: hay que recrear vía connect() completo.
    if (this.registerer.state === SIP.RegistererState.Terminated) {
      return false;
    }

    try {
      await this.registerer.register();
    } catch {
      // retry/full reconnect lo maneja el caller
    }
    return this.syncRegistrationFromRegisterer();
  }

  clearReconnectTimers() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.registerRetryTimer) {
      clearTimeout(this.registerRetryTimer);
      this.registerRetryTimer = null;
    }
  }

  scheduleRegisterRetry(delaySeconds = 2) {
    if (!this.shouldBeConnected || !this.registerer) return;
    if (this.registerRetryTimer) return;
    const delayMs = Math.max(0, delaySeconds * 1000);
    this.registerRetryTimer = setTimeout(async () => {
      this.registerRetryTimer = null;
      if (!this.shouldBeConnected || !this.registerer) return;
      if (this.syncRegistrationFromRegisterer()) return;
      try {
        await this.registerer.register();
        this.syncRegistrationFromRegisterer();
      } catch (e) {
        const retryAfter = this.registerer?.retryAfter;
        this.scheduleRegisterRetry(typeof retryAfter === 'number' ? retryAfter : 4);
      }
    }, delayMs);
  }

  scheduleReconnect() {
    if (!this.shouldBeConnected || !this.userAgent) return;
    if (this.attemptingReconnection) return;
    this.attemptingReconnection = true;

    const attempt = ++this.reconnectAttempt;
    const base = 2;
    const max = 30;
    const delaySeconds = Math.min(max, attempt === 1 ? 0 : base * Math.pow(1.5, attempt - 2));

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      if (!this.shouldBeConnected || !this.userAgent) {
        this.attemptingReconnection = false;
        return;
      }
      try {
        await Promise.resolve().then(() => this.userAgent.reconnect());
        this.attemptingReconnection = false;
        if (!this.syncRegistrationFromRegisterer()) {
          this.scheduleRegisterRetry(0);
        }
      } catch (e) {
        this.attemptingReconnection = false;
        this.scheduleReconnect();
      }
    }, delaySeconds * 1000);
  }

  async connect({ sip_ws_url: sipWsUrl, extension, auth_user: authUser, auth_password: authPassword } = {}) {
    if (!sipWsUrl || !extension) {
      throw new Error('Config SIP incompleta: sip_ws_url/extension requeridos');
    }
    if (!authPassword) {
      throw new Error('Config SIP incompleta: auth_password requerido');
    }

    // En adelante, mantenemos intención de conexión
    this.shouldBeConnected = true;
    this.reconnectAttempt = 0;
    this.clearReconnectTimers();
    this.setConnectionStatus(CONNECTION_STATUS.CONNECTING);

    // Si existe un UA previo, lo apagamos sin perder la intención de conexión.
    await this.disconnect({ preserveIntent: true });

    const wsUrl = new URL(sipWsUrl);
    const domain = wsUrl.hostname;
    const uri = SIP.UserAgent.makeURI(`sip:${extension}@${domain}`);
    if (!uri) throw new Error('No se pudo construir URI SIP');

    const uaOptions = {
      uri,
      transportOptions: {
        server: sipWsUrl,
        keepAliveInterval: 30,
        keepAliveDebounce: 10,
        connectionTimeout: 10,
        traceSip: false,
      },
      authorizationUsername: (authUser || '').trim() || extension,
      authorizationPassword: authPassword,
      displayName: `Agente ${extension}`,
      // sip.js only honors mediaStreamFactory as arg to defaultSessionDescriptionHandlerFactory,
      // not as a key inside sessionDescriptionHandlerFactoryOptions (that was a no-op in #273).
      sessionDescriptionHandlerFactory: SIP.Web.defaultSessionDescriptionHandlerFactory(
        constraints => {
          const wantsAudio = !constraints || constraints.audio;
          const liveAudio = this.localStream
            ?.getAudioTracks?.()
            .some(t => t.readyState === 'live');
          if (wantsAudio && liveAudio) {
            return Promise.resolve(this.localStream);
          }
          const streamPromise = this.getUserMediaCompat(
            constraints || { audio: true, video: false }
          );
          return (
            streamPromise ||
            Promise.reject(new Error('Media devices not available in insecure contexts.'))
          );
        }
      ),
      sessionDescriptionHandlerFactoryOptions: {
        // sip.js waits for ICE gathering before sending the SDP answer.
        // Default is 5000ms; when gathering never hits "complete" early (common
        // with STUN), Contestar→Established is always ~5s of silence after mic is ready.
        // Cap wait so we send with host/srflx gathered so far (STUN usually <300ms).
        iceGatheringTimeout: 800,
        peerConnectionConfiguration: {
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        },
      },
      delegate: {
        onInvite: invitation => this.handleIncomingCall(invitation),
        onConnect: () => {
          // Tras WS drop, el Registerer puede seguir Registered sin nuevo stateChange.
          if (this.syncRegistrationFromRegisterer()) return;
          this.scheduleRegisterRetry(0);
        },
        onDisconnect: error => {
          this.setRegistered(false);
          if (this.shouldBeConnected) {
            this.setConnectionStatus(CONNECTION_STATUS.CONNECTING, error);
            this.scheduleReconnect();
          } else {
            this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED, error);
          }
        },
      },
    };

    this.userAgent = new SIP.UserAgent(uaOptions);
    await this.userAgent.start();

    this.registerer = new SIP.Registerer(this.userAgent, {
      expires: 600,
      refreshFrequency: 99,
    });

    this.registerer.stateChange.addListener(newState => {
      if (newState === SIP.RegistererState.Registered) {
        this.setRegistered(true);
        this.setConnectionStatus(CONNECTION_STATUS.CONNECTED);
      } else if (newState === SIP.RegistererState.Unregistered) {
        this.setRegistered(false);
        if (this.shouldBeConnected) {
          this.setConnectionStatus(CONNECTION_STATUS.CONNECTING);
          const retryAfter = this.registerer?.retryAfter;
          this.scheduleRegisterRetry(typeof retryAfter === 'number' ? retryAfter : 2);
          this.scheduleReconnect();
        } else {
          this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
        }
      }
    });

    try {
      await this.registerer.register();
      this.syncRegistrationFromRegisterer();
    } catch (e) {
      this.setConnectionStatus(CONNECTION_STATUS.ERROR, e);
      throw e;
    }

    // online hook: si vuelve red, reconectar
    if (!this._onlineHandler) {
      this._onlineHandler = () => {
        if (this.shouldBeConnected) this.scheduleReconnect();
      };
      window.addEventListener('online', this._onlineHandler);
    }
  }

  async disconnect({ preserveIntent = false } = {}) {
    if (!preserveIntent) this.shouldBeConnected = false;
    this.clearReconnectTimers();

    this.cleanupCall();

    try {
      if (this.registerer) {
        try {
          await this.registerer.unregister();
        } catch {
          // ignore
        }
      }
    } finally {
      this.registerer = null;
    }

    try {
      if (this.userAgent) {
        try {
          await this.userAgent.stop();
        } catch {
          // ignore
        }
      }
    } finally {
      this.userAgent = null;
    }

    this.setRegistered(false);
    this.setConnectionStatus(CONNECTION_STATUS.DISCONNECTED);
  }

  handleIncomingCall(invitation) {
    if (this.primarySession) {
      this.rejectInboundInvitation(invitation);
      return;
    }

    const chosen = this._availabilityProvider ? this._availabilityProvider() : 'online';
    const autoCode = inboundAutoRejectStatusCode(chosen);
    if (autoCode) {
      this.rejectInboundInvitation(invitation, { statusCode: autoCode, reasonPhrase: 'Busy Here' });
      return;
    }

    this.primarySession = invitation;

    const caller = invitation?.remoteIdentity?.uri?.user || 'Desconocido';
    const callTypeHeader = invitation?.request?.getHeader?.('X-Chatwoot-Call-Type');
    const didHeader = invitation?.request?.getHeader?.('X-Chatwoot-DID');
    const callIdHeader = invitation?.request?.getHeader?.('X-Chatwoot-Call-Id');
    const waCallIdHeader = invitation?.request?.getHeader?.('X-Chatwoot-Wa-Call-Id');
    const transferType = invitation?.request?.getHeader?.('X-Chatwoot-Transfer');
    const transferConversationId = invitation?.request?.getHeader?.('X-Chatwoot-Conversation-Id');
    const originalCaller = invitation?.request?.getHeader?.('X-Chatwoot-Original-Caller');
    const callTypeRaw = (callTypeHeader || '').toString().trim().toLowerCase();
    const callType = callTypeRaw === 'sip' || callTypeRaw === 'whatsapp' ? callTypeRaw : null;
    const didNumber = (didHeader || '').toString().trim() || null;
    const callId = (callIdHeader || '').toString().trim() || null;
    const waCallId = (waCallIdHeader || '').toString().trim() || null;
    const isAttendedTransfer = transferType === 'attended-auto';
    const remoteNumber =
      isAttendedTransfer && originalCaller
        ? originalCaller.toString().trim()
        : caller;
    this._transferIncomingContext = isAttendedTransfer
      ? { conversationId: Number(transferConversationId) || null }
      : null;
    this.currentDirection = 'inbound';
    this.currentRemoteNumber = remoteNumber;
    this.emit('call:incoming', {
      remoteNumber,
      callType,
      callId,
      didNumber,
      waCallId,
      transferContext: this._transferIncomingContext,
    });
    this.emit('call:state', {
      state: CALL_STATE.RINGING,
      remoteNumber: caller,
      direction: this.currentDirection,
    });

    // Pre-calentar micrófono mientras timbra para que "Contestar" sea inmediato.
    // No bloquea UI; si falla, se reintentará al contestar.
    if (!this._micWarmupPromise) {
      this._micWarmupPromise = this.ensureMicrophonePermission().catch(() => null);
    }

    this.setupSession(invitation);
  }

  // ponytail: sesión Terminated que no pasó por cleanup deja currentSession zombie y bloquea el 2º dial
  discardInactiveSession() {
    const session = this.primarySession;
    if (!session) return;
    if (session.state === SIP.SessionState.Terminated) {
      this.cleanupCall(session);
    }
  }

  async dial(target, { didNumber, callType, callId } = {}) {
    const number = (target || '').toString().trim();
    if (!number) throw new Error('Número inválido');
    if (!this.userAgent) throw new Error('SIP no conectado');
    this.syncRegistrationFromRegisterer();
    if (!this.isRegistered) throw new Error('SIP no registrado');
    this.discardInactiveSession();
    if (this.primarySession) throw new Error('Ya hay una llamada activa');

    const wsUrl = new URL(this.userAgent.configuration.transportOptions.server);
    const domain = wsUrl.hostname;
    const targetUri = SIP.UserAgent.makeURI(`sip:${number}@${domain}`);
    if (!targetUri) throw new Error('No se pudo construir URI destino');

    // earlyMedia=true permite reproducir audio remoto en respuestas provisionales (183+SDP),
    // útil para ringback real desde Asterisk.
    //
    // IMPORTANTE (sip.js):
    // Los headers del INVITE inicial deben ir en `InviterOptions.extraHeaders` (constructor),
    // no en `invite({ requestOptions })`, para que Asterisk los reciba (incluyendo el retry tras 401).
    const normalizedCallType = (callType || 'sip').toString().trim().toLowerCase() || 'sip';
    const inviteHeaders = [];
    const normalizedCallId = (callId || '').toString().trim();
    if (didNumber) inviteHeaders.push(`X-Chatwoot-DID: ${didNumber}`);
    inviteHeaders.push(`X-Chatwoot-Call-Type: ${normalizedCallType}`);
    if (normalizedCallId) inviteHeaders.push(`X-Chatwoot-Call-Id: ${normalizedCallId}`);

    const inviter = new SIP.Inviter(this.userAgent, targetUri, {
      earlyMedia: true,
      extraHeaders: inviteHeaders,
    });
    this.primarySession = inviter;
    this.currentDirection = 'outbound';
    this.currentRemoteNumber = number;
    this.endedReason = null;
    this.lastSipStatusCode = null;
    this.lastSipReasonPhrase = null;
    this._callEndedForSessionId = null;
    this.emit('call:state', {
      state: CALL_STATE.RINGING,
      remoteNumber: number,
      direction: this.currentDirection,
    });
    this.setupSession(inviter);

    this.unlockAudioPlayback();
    try {
      // Si no hay micrófono (p.ej. HTTP), sip.js puede terminar negociando recvonly/silencio.
      // Validamos explícitamente para evitar "me oyen en silencio" y para que mute tenga track.
      await this.ensureMicrophonePermission();
      // Si un Terminated tardío de la llamada anterior corrió cleanup mientras pedíamos mic, abortar.
      if (this.currentSession !== inviter) {
        throw new Error('La sesión de llamada fue invalidada antes de enviar INVITE');
      }
      // ringback local (fallback). Si llega early media, lo detenemos.
      this.startRingback();
      await inviter.invite({
        requestDelegate: {
          onProgress: response => {
            // Ignore provisional after cancel/hangup (otherwise 180 restarts local ringback
            // or 183 re-binds early media while we are tearing down).
            if (this.primarySession !== inviter) return;
            if (
              inviter.state === SIP.SessionState.Terminated ||
              inviter.state === SIP.SessionState.Terminating
            ) {
              return;
            }
            const status = response?.message?.statusCode;
            // 183 Session Progress con SDP: early media (ringback remoto del trunk)
            if (status === 183 && response?.message?.body) {
              this.stopRingback();
              this.bindRemoteAudio(inviter);
              return;
            }
            // 180 Ringing: local ringback only if we are not already on early media
            if (status === 180 && !this._remoteAudioBoundPc) {
              this.startRingback();
            }
          },
          onReject: response => {
            const statusCode = response?.message?.statusCode || null;
            const reasonPhrase = response?.message?.reasonPhrase || null;
            this.lastSipStatusCode = statusCode;
            this.lastSipReasonPhrase = reasonPhrase;
            this.endedReason = this.mapSipStatusToReason(statusCode);
            this.silenceCallAudio();

            // IMPORTANTE:
            // inviter.invite() se resuelve al enviar INVITE; si el remoto cuelga/rechaza antes de contestar,
            // necesitamos cerrar la UI aquí mismo (sin depender solo de SessionState.Terminated).
            this.emitCallEndedOnce({ session: inviter, durationSec: 0 });
            this.cleanupCall(inviter);
          },
          onAccept: () => {
            this.stopRingback();
          },
        },
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });
      return true;
    } catch (e) {
      this.cleanupCall(inviter);
      throw e;
    }
  }

  async answer() {
    if (!this.currentSession) return;
    // Feedback inmediato: detener timbrado local y desbloquear audio.
    this.stopRingback();
    this.unlockAudioPlayback();
    if (this._micWarmupPromise) {
      await this._micWarmupPromise;
    } else {
      await this.ensureMicrophonePermission();
    }
    await this.currentSession.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });
  }

  hangup() {
    this._transferAbort = true;
    this.terminateTransferSession();
    // Kill local WebAudio + early-media RTP audio before CANCEL/BYE completes.
    // PSTN ringback is often in-band (183); waiting for Terminated leaves the agent hearing it.
    this.silenceCallAudio();

    if (!this.primarySession) return;
    this.endedReason = this.endedReason || 'hangup';
    const session = this.primarySession;
    try {
      if (session.state === SIP.SessionState.Established) {
        session.bye();
      } else if (this.currentDirection === 'inbound' && typeof session.reject === 'function') {
        // For inbound ringing calls, send an explicit final decline so upstream
        // does not keep retrying/ringing the same call attempt.
        this.endedReason = 'rejected';
        this.rejectInboundInvitation(session);
      } else if (typeof session.cancel === 'function') {
        if (this.currentDirection === 'outbound') this.endedReason = 'cancelled';
        session.cancel();
      } else if (typeof session.reject === 'function') {
        session.reject();
      }
    } catch {
      // Señalización SIP falló; forzamos teardown local abajo.
    }

    // ponytail: si CANCEL/BYE no llega a Terminated (WS a medias), la sesión zombie bloquea el próximo dial
    if (this._hangupForceTimer) clearTimeout(this._hangupForceTimer);
    this._hangupForceTimer = setTimeout(() => {
      this._hangupForceTimer = null;
      if (this.primarySession !== session) return;
      const durationSec = this.callStartTime
        ? Math.max(0, Math.floor((Date.now() - this.callStartTime) / 1000))
        : 0;
      this.emitCallEndedOnce({ session, durationSec });
      this.cleanupCall(session);
    }, 2000);
  }

  async setPrimaryHold(held) {
    const session = this.primarySession;
    if (!session || session.state !== SIP.SessionState.Established) {
      throw new Error('No hay llamada conectada para transferir');
    }

    // Root cause: holdModifier is also applied to the 200 OK *answer* (sip.js
    // reuses sessionDescriptionHandlerModifiersReInvite in setAnswer). That
    // rewrites Asterisk's a=recvonly → a=inactive. Unhold then offers
    // inactive→recvonly (updateDirection), never a=sendrecv, so Asterisk keeps MOH.
    // Use SDH hold flag like SessionManager — native sendonly/sendrecv, no munging.
    const pc = session.sessionDescriptionHandler?.peerConnection;
    if (!held && pc) {
      pc.getTransceivers().forEach(t => {
        if (t.direction && t.direction !== 'stopped') {
          t.direction = 'sendrecv';
        }
      });
    }

    const sdhOptions = {
      ...(session.sessionDescriptionHandlerOptionsReInvite || {}),
      hold: !!held,
    };

    await new Promise((resolve, reject) => {
      session
        .invite({
          sessionDescriptionHandlerOptions: sdhOptions,
          // never holdModifier: it munges the answer and leaves PC in inactive
          sessionDescriptionHandlerModifiers: [],
          requestDelegate: {
            onAccept: () => resolve(),
            onReject: response => {
              const code = response?.message?.statusCode || '?';
              reject(new Error(`Hold/unhold rechazado (${code})`));
            },
          },
        })
        .catch(reject);
    });

    if (pc) {
      pc.getReceivers().forEach(r => {
        if (r.track) r.track.enabled = !held;
      });
      pc.getSenders().forEach(s => {
        if (s.track) s.track.enabled = !held;
      });
    }
  }

  terminateTransferSession() {
    const session = this.transferSession;
    this.transferSession = null;
    if (!session) return;
    // ponytail: cancel/bye on Terminated throws (often as rejected Promise → Uncaught)
    if (
      session.state === SIP.SessionState.Terminated ||
      session.state === SIP.SessionState.Terminating
    ) {
      return;
    }
    try {
      if (session.state === SIP.SessionState.Established) {
        session.bye();
      } else if (
        session.state === SIP.SessionState.Establishing &&
        typeof session.cancel === 'function'
      ) {
        Promise.resolve(session.cancel()).catch(() => {});
      }
    } catch {
      // ignore
    }
  }

  waitForTransferTarget(target, onProgress) {
    const TRANSFER_TARGET_TIMEOUT_MS = 60000;
    const emitProgress = typeof onProgress === 'function' ? onProgress : () => {};

    return new Promise((resolve, reject) => {
      let settled = false;
      let timer = null;
      let onStateChange = null;
      let rejectCause = null;
      let rejectMessage = null;

      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        if (onStateChange && typeof target.stateChange?.removeListener === 'function') {
          try {
            target.stateChange.removeListener(onStateChange);
          } catch (e) {
            // ignore
          }
        }
        fn(value);
      };

      const rejectWithCause = (cause, message) => {
        const err = new Error(message);
        err.cause = cause;
        finish(reject, err);
      };

      timer = setTimeout(() => {
        rejectWithCause('timeout', 'El agente no contestó la transferencia');
      }, TRANSFER_TARGET_TIMEOUT_MS);

      onStateChange = newState => {
        if (newState === SIP.SessionState.Established) {
          finish(resolve);
          return;
        }
        if (newState !== SIP.SessionState.Terminated) return;
        // ponytail: Terminated often races ahead of onReject; defer so 603 maps to rejected
        queueMicrotask(() => {
          if (settled) return;
          if (this._transferAbort) {
            rejectWithCause('aborted', 'Transferencia cancelada');
            return;
          }
          rejectWithCause(
            rejectCause || 'failed',
            rejectMessage || 'La consulta de transferencia finalizó'
          );
        });
      };

      if (typeof target.stateChange?.addListener === 'function') {
        target.stateChange.addListener(onStateChange);
      }

      target
        .invite({
          requestDelegate: {
            onProgress: response => {
              const statusCode = response?.message?.statusCode;
              if (statusCode >= 180 && statusCode < 200) {
                emitProgress({ phase: 'ringing', statusCode });
              }
            },
            onReject: response => {
              const statusCode = response?.message?.statusCode || null;
              rejectCause = referNotifyCause(statusCode) || 'failed';
              rejectMessage =
                rejectCause === 'rejected'
                  ? 'El agente rechazó la transferencia'
                  : rejectCause === 'no_answer'
                    ? 'El agente no contestó la transferencia'
                    : 'No se pudo contactar al agente';
              rejectWithCause(rejectCause, rejectMessage);
            },
          },
          sessionDescriptionHandlerOptions: {
            constraints: { audio: true, video: false },
          },
        })
        .catch(err => {
          const error = err instanceof Error ? err : new Error('No se pudo transferir');
          if (!error.cause) error.cause = 'failed';
          finish(reject, error);
        });
    });
  }

  finalizeTransferredCall() {
    if (!this._transferCompletionPending) return;
    this._transferCompletionPending = false;
    this._transferPrimary = null;
    this._transferIncomingContext = null;
    this.stopCallTimer();
    this.currentDirection = null;
    this.currentRemoteNumber = null;
    this.emit('call:muted', { isMuted: false });
    this.emit('call:state', { state: CALL_STATE.IDLE });
    this.emit('call:duration', { durationSec: 0 });
  }

  async transferToExtension(extension, { metadata = {}, onProgress } = {}) {
    const ext = (extension || '').toString().trim();
    if (!ext) throw new Error('Extensión vacía');
    const primary = this.primarySession;
    if (!primary || primary.state !== SIP.SessionState.Established) {
      throw new Error('No hay llamada conectada');
    }
    if (!this.userAgent) throw new Error('SIP no conectado');
    if (typeof primary.refer !== 'function') {
      throw new Error('Transferencia no soportada en esta llamada');
    }

    const wsUrl = new URL(this.userAgent.configuration.transportOptions.server);
    const domain = wsUrl.hostname;
    const targetUri = SIP.UserAgent.makeURI(`sip:${ext}@${domain}`);
    if (!targetUri) throw new Error('URI inválida');

    const headers = [
      'X-Chatwoot-Transfer: attended-auto',
      `X-Chatwoot-Conversation-Id: ${metadata.conversationId || ''}`,
      `X-Chatwoot-Call-Id: ${metadata.callId || ''}`,
      `X-Chatwoot-Call-Type: ${metadata.callType || 'sip'}`,
      `X-Chatwoot-Original-Caller: ${metadata.originalCaller || this.currentRemoteNumber || ''}`,
    ];
    if (metadata.didNumber) headers.push(`X-Chatwoot-DID: ${metadata.didNumber}`);
    if (metadata.waCallId) headers.push(`X-Chatwoot-Wa-Call-Id: ${metadata.waCallId}`);

    this._transferAbort = false;
    // ponytail: only mark transfer-primary once REFER starts — earlier swallows reject/rollback Terminated
    this._transferPrimary = null;
    await this.setPrimaryHold(true);

    const target = new SIP.Inviter(this.userAgent, targetUri, {
      earlyMedia: false,
      extraHeaders: headers,
    });
    this.transferSession = target;

    try {
      await this.waitForTransferTarget(target, onProgress);
      onProgress?.({ phase: 'completing' });
      target.sessionDescriptionHandler?.peerConnection
        ?.getSenders()
        .filter(sender => sender.track?.kind === 'audio')
        .forEach(sender => {
          sender.track.enabled = false;
        });
      this._transferPrimary = primary;
      await primary.refer(target);
      // ponytail: Asterisk may delay BYE to transferor; REFER accept = success (waiting Terminated caused false rollback)
      this._transferCompletionPending = true;
      this.primarySession = null;
      this.transferSession = null;
    } catch (error) {
      onProgress?.({ phase: 'rolling_back' });
      this._transferPrimary = null;
      this.terminateTransferSession();
      if (primary.state === SIP.SessionState.Established) {
        try {
          await this.setPrimaryHold(false);
          this.bindRemoteAudio(primary);
        } catch (resumeError) {
          resumeError.cause = 'resume_failed';
          throw resumeError;
        }
      }
      throw error;
    }
  }

  rejectInboundInvitation(invitation, { statusCode = 603, reasonPhrase = 'Decline' } = {}) {
    invitation.reject({
      statusCode,
      reasonPhrase,
    });
  }

  toggleMute() {
    if (!this.currentSession) return false;
    const pc = this.currentSession.sessionDescriptionHandler?.peerConnection;
    if (!pc) return false;

    const nextMuted = !this.isMuted;
    const senders = pc.getSenders();
    const audioSenders = senders.filter(s => s.track?.kind === 'audio');
    if (audioSenders.length === 0) {
      return false;
    }

    audioSenders.forEach(sender => {
      if (sender.track?.kind === 'audio') {
        sender.track.enabled = !nextMuted;
      }
    });
    this.isMuted = nextMuted;
    this.emit('call:muted', { isMuted: this.isMuted });
    return this.isMuted;
  }

  setupSession(session) {
    session.stateChange.addListener(newState => {
      if (
        newState === SIP.SessionState.Terminating ||
        newState === SIP.SessionState.Terminated
      ) {
        // Only silence when this session owns (or already released) the primary call.
        // Transfer consult sessions must not tear down the held primary's early/remote media.
        if (this.primarySession === session || this.primarySession === null) {
          this.silenceCallAudio();
        }
      }

      // Ignorar transferSession y sesiones ya reemplazadas (salvo primary en handoff REFER).
      if (
        this.primarySession !== session &&
        this._transferPrimary !== session
      ) {
        return;
      }

      if (newState === SIP.SessionState.Established) {
        if (this.primarySession !== session) return;
        if (!this._transferIncomingContext) {
          const remoteNumber =
            this.primarySession?.remoteIdentity?.uri?.user ||
            this.primarySession?.remoteIdentity?.displayName ||
            null;

          if (remoteNumber) this.currentRemoteNumber = remoteNumber;
        }
        this.emit('call:state', {
          state: CALL_STATE.CONNECTED,
          remoteNumber: this.currentRemoteNumber,
          direction: this.currentDirection,
        });
        this.stopRingback();
        this.startCallTimer();

        this.bindRemoteAudio(session);
      }

      if (newState === SIP.SessionState.Terminated) {
        if (this._transferPrimary === session) {
          this.stopCallTimer();
          if (this.primarySession === session) this.primarySession = null;
          this._transferPrimary = null;
          return;
        }

        const endedAt = Date.now();
        const durationSec = this.callStartTime
          ? Math.max(0, Math.floor((endedAt - this.callStartTime) / 1000))
          : 0;
        // stopRingback already ran above (before primarySession guard).
        this.emitCallEndedOnce({ session, durationSec });
        this.cleanupCall(session);
      }
    });
  }

  stopCallTimer() {
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
    this.callStartTime = null;
  }

  startCallTimer() {
    this.callStartTime = Date.now();
    if (this.callTimerInterval) clearInterval(this.callTimerInterval);
    this.callTimerInterval = setInterval(() => {
      const durationSec = Math.max(0, Math.floor((Date.now() - this.callStartTime) / 1000));
      this.emit('call:duration', { durationSec });
    }, 1000);
  }

  cleanupCall(session = null) {
    // Si nos pasan la sesión que terminó y ya hay otra activa, no tocar la nueva.
    if (session && this.primarySession && this.primarySession !== session) {
      return;
    }

    if (this._hangupForceTimer) {
      clearTimeout(this._hangupForceTimer);
      this._hangupForceTimer = null;
    }

    this.stopCallTimer();
    this.isMuted = false;
    this.silenceCallAudio();
    this._callEndedForSessionId = null;
    this._micWarmupPromise = null;
    this._transferIncomingContext = null;
    this._transferCompletionPending = false;
    this._transferPrimary = null;
    this.terminateTransferSession();

    // Cerrar tracks del PeerConnection (sip.js) para no dejar el mic bloqueado al 2º dial.
    const pc =
      (session || this.primarySession)?.sessionDescriptionHandler?.peerConnection || null;
    if (pc) {
      try {
        pc.getSenders().forEach(sender => {
          try {
            sender.track?.stop();
          } catch {
            // ignore
          }
        });
        pc.getReceivers().forEach(receiver => {
          try {
            receiver.track?.stop();
          } catch {
            // ignore
          }
        });
        pc.close();
      } catch {
        // ignore
      }
    }

    // Liberar micrófono si lo habíamos solicitado explícitamente
    if (this.localStream) {
      try {
        this.localStream.getTracks().forEach(t => t.stop());
      } catch {
        // ignore
      }
      this.localStream = null;
    }
    this.currentDirection = null;
    this.currentRemoteNumber = null;
    this.endedReason = null;
    this.lastSipStatusCode = null;
    this.lastSipReasonPhrase = null;

    this.primarySession = null;
    this.emit('call:muted', { isMuted: false });
    this.emit('call:state', { state: CALL_STATE.IDLE });
    this.emit('call:duration', { durationSec: 0 });
  }

  getStatus() {
    return {
      connectionStatus: this.connectionStatus,
      isRegistered: this.isRegistered,
      hasUserAgent: !!this.userAgent,
      hasSession: !!this.currentSession,
    };
  }
}

export { SipClient };
export default SipClient;

